import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * Matches parsing logic in ConfidentialityAgreement.jsx
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseConfidentialityAgreementFromApiResponse(
  response,
  targetUserId,
) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.agreement) {
    return result.agreement;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.applicantName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/**
 * Print-ready HTML for Confidentiality Agreement (completed forms).
 * Body text aligned with ConfidentialityAgreement.jsx
 * @param {Record<string, unknown>} agreement API record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildConfidentialityAgreementPrintHtml(agreement, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(agreement.employeeName || agreement.applicantName || ""),
  );
  const position = escapeHtml(
    String(agreement.positionAppliedFor || agreement.position || ""),
  );
  const agreementDate = escapeHtml(formatDate(agreement.agreementDate));

  const es = agreement.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    agreement.applicantSignature ||
    agreement.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      agreement.applicantSignedAt ||
        agreement.applicantSignatureDate ||
        agreement.empSignedAt,
    ),
  );

  const reviewedBySignedBy = escapeHtml(
    String(agreement.reviewedBySignedBy || ""),
  );
  const reviewedBySig =
    agreement.reviewedBySignature || agreement.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      agreement.reviewedBySignedAt ||
        agreement.adminSignatureDate ||
        agreement.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!agreement.applicantStatementAccepted;
  const statusCompleted =
    String(agreement.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Confidentiality Agreement — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 16px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 13px; font-weight: 500; color: #2563eb; margin: 0; }
    h2.section-title {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 8px;
      margin: 20px 0 12px 0;
    }
    .auth-box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 16px;
      margin-top: 8px;
    }
    .auth-box p {
      margin: 0 0 12px 0;
      text-align: justify;
      font-size: 11px;
      color: #374151;
    }
    .auth-box p:last-child { margin-bottom: 0; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px 20px;
      margin-bottom: 16px;
    }
    .field-label {
      font-size: 10px;
      font-weight: 600;
      color: #6b7280;
      margin-bottom: 4px;
    }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 38px;
      background: #fff;
      font-size: 12px;
      color: #111827;
    }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 {
      font-size: 12px;
      font-weight: 700;
      margin: 0 0 10px 0;
      color: #111827;
    }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 24px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Confidentiality of Information Agreement</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="grid">
      <div>
        <div class="field-label">Employee name</div>
        <div class="field-value">${employeeName || "—"}</div>
      </div>
      <div>
        <div class="field-label">Position</div>
        <div class="field-value">${position || "—"}</div>
      </div>
      <div>
        <div class="field-label">Agreement date</div>
        <div class="field-value">${agreementDate || "—"}</div>
      </div>
    </div>

    <h2 class="section-title">Terms</h2>
    <div class="auth-box">
      <p>I agree to the following terms as an employee of this Agency.</p>
      <p>
        I understand that during my employment with this Agency, I will
        have access to confidential patient/family, agency, and
        personnel information. I agree that all patient/family
        information must be held in strict confidence and used only as
        necessary in performing my job responsibilities. I will not
        communicate client information to other clients or to anyone not
        involved in their care, including family members,
        non-professional personnel, other organizations, the news media,
        or the general public. I will not communicate negatively about
        this Agency or its employees. Any communication with clients
        requires prior written consent from the client or their legal
        guardian. I understand that a breach of this agreement will
        result in termination and possible civil actions.
      </p>
      <p>
        I further agree to abide by this Agency's Health Insurance
        Portability and Accountability Act Policies (HIPAA).
      </p>
    </div>

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${employeeName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
